import { hashCanonical } from '../entropy/hash';
import type { Collector } from './types';
import { safe, short, signal } from './util';

/** Expressions whose last digits differ subtly between JavaScript engines and math libraries. */
export const MATH_EXPRESSIONS: ReadonlyArray<readonly [string, () => number]> = [
  ['Math.tan(-1e300)', () => Math.tan(-1e300)],
  ['Math.sin(-1e300)', () => Math.sin(-1e300)],
  ['Math.cos(10.000000000123)', () => Math.cos(10.000000000123)],
  ['Math.expm1(1)', () => Math.expm1(1)],
  ['Math.exp(1)', () => Math.exp(1)],
  ['Math.cosh(1)', () => Math.cosh(1)],
  ['Math.sinh(1)', () => Math.sinh(1)],
  ['Math.tanh(1)', () => Math.tanh(1)],
  ['Math.log1p(10)', () => Math.log1p(10)],
  ['Math.pow(Math.PI, -100)', () => Math.pow(Math.PI, -100)],
  ['Math.acos(0.123124234234234242)', () => Math.acos(0.123124234234234242)],
  ['Math.acosh(1e308)', () => Math.acosh(1e308)],
  ['Math.asinh(1)', () => Math.asinh(1)],
  ['Math.atanh(0.5)', () => Math.atanh(0.5)],
  ['Math.atan2(0.04, -0.3)', () => Math.atan2(0.04, -0.3)],
  ['Math.cbrt(100)', () => Math.cbrt(100)],
];

/** Strings chosen to exercise accents, case, ligatures, digits and scripts in the collation table. */
export const COLLATION_SAMPLE = ['a', 'A', 'á', 'ä', 'æ', 'ae', 'ß', 'ss', 'z', 'Z', 'ı', 'i', 'İ', '1', '10', '2', 'ﬁ', 'fi', 'Ω', 'ω', '中', '日', 'ǆ', 'ö', 'o', 'ø', 'ch', 'c', 'h'];

export const mathCollector: Collector = {
  id: 'math',
  title: 'Math & engine quirks',
  description: 'Tiny differences in how your JavaScript engine computes math, reports errors and sorts text.',
  async collect() {
    return Promise.all([
      safe('math.fp', 'Floating-point results', async () => {
        const results = Object.fromEntries(MATH_EXPRESSIONS.map(([expr, fn]) => [expr, String(fn())]));
        const hash = await hashCanonical(results);
        return signal('math.fp', 'Floating-point results', { sha256: hash, results }, {
          display: `${short(hash)}…\n${Object.entries(results)
            .map(([k, v]) => `${k} = ${v}`)
            .join('\n')}`,
        });
      }),

      safe('math.errorStack', 'Error message & stack format', () => {
        let message = '';
        try {
          // Deliberate TypeError: the wording identifies the engine. JSON.parse keeps minifiers from folding it.
          const nothing = JSON.parse('null') as { x: number };
          nothing.x.toString();
        } catch (err) {
          message = err instanceof Error ? err.message : String(err);
        }
        const stack = new Error('probe').stack ?? '';
        const style = /^\s+at /m.test(stack) ? 'V8-style ("    at fn (file:line:col)")' : /@/.test(stack) ? '"fn@file:line:col" style' : 'unknown';
        const value = { typeErrorMessage: message, stackStyle: style, stackTraceLimit: (Error as unknown as { stackTraceLimit?: number }).stackTraceLimit ?? null };
        return signal('math.errorStack', 'Error message & stack format', value, {
          display: `TypeError: "${message}"\nstack: ${style}${value.stackTraceLimit !== null ? ` · stackTraceLimit ${value.stackTraceLimit}` : ''}`,
        });
      }),

      safe('math.evalLength', 'eval.toString().length', () => signal('math.evalLength', 'eval.toString().length', globalThis.eval.toString().length)),

      safe('math.collator', 'Text sorting (Intl.Collator)', async () => {
        const order = [...COLLATION_SAMPLE].sort(new Intl.Collator().compare);
        const hash = await hashCanonical(order);
        return signal('math.collator', 'Text sorting (Intl.Collator)', order, {
          display: `${short(hash)}… · ${order.join(' ')}`,
        });
      }),
    ]);
  },
};
